import React, { useEffect, useState } from "react";
import { AlertCircle, Inbox } from "lucide-react";

const DEFAULT_VISIBLE_COUNT = 4;

const DashboardRecentSection = ({
    title,
    icon: Icon,
    items,
    status,
    error,
    errorMessage,
    emptyTitle,
    emptyDescription,
    getItemKey,
    renderItem,
    initialCount = DEFAULT_VISIBLE_COUNT,
    scrollAllItems = false,
    heightClassName = "h-[330px]",
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const safeItems = Array.isArray(items) ? items : [];
    const canExpand = !scrollAllItems && safeItems.length > initialCount;
    const showExpandedContent = canExpand && isExpanded;
    const shouldShowAllItems = scrollAllItems || showExpandedContent;
    const visibleItems = shouldShowAllItems
        ? safeItems
        : safeItems.slice(0, initialCount);
    const isLoading = status === "idle" || status === "loading";
    const hasError = status === "failed" || Boolean(error);
    const errorDetail = typeof error === "string" ? error : "";

    useEffect(() => {
        if (!canExpand && isExpanded) {
            setIsExpanded(false);
        }
    }, [canExpand, isExpanded]);

    return (
        <section
            className={`flex ${heightClassName} flex-col overflow-hidden rounded-2xl border bg-white shadow-sm transition-all duration-300 ease-out ${
                showExpandedContent
                    ? "border-indigo-300 ring-1 ring-indigo-100"
                    : "border-slate-200"
            }`}
        >
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-indigo-100 bg-[#eef2ff] px-4 py-4 sm:px-5">
                <div className="flex min-w-0 items-center gap-2.5">
                    {Icon && (
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/80 text-indigo-600 ring-1 ring-indigo-100">
                            <Icon size={18} aria-hidden="true" />
                        </span>
                    )}
                    <h2 className="truncate text-base font-semibold text-[#0f172a]">
                        {title}
                    </h2>
                </div>

                {canExpand && !isLoading && !hasError && (
                    <button
                        type="button"
                        onClick={() => setIsExpanded((current) => !current)}
                        aria-expanded={showExpandedContent}
                        className="shrink-0 rounded-lg border border-transparent px-2.5 py-1.5 text-xs font-semibold text-indigo-600 transition hover:border-indigo-100 hover:bg-indigo-50 hover:text-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-200"
                    >
                        {showExpandedContent ? "Show less" : "View all"}
                    </button>
                )}
            </div>

            <div className="min-h-0 flex-1 overflow-hidden">
                {isLoading && (
                    <div
                        className="h-full space-y-1 p-2"
                        aria-label={`Loading ${title}`}
                    >
                        {Array.from({ length: 5 }).map((_, index) => (
                            <div
                                key={`skeleton-${index}`}
                                className="flex animate-pulse items-center gap-3 border-b border-slate-100 px-3 py-3 last:border-b-0"
                            >
                                <div className="min-w-0 flex-1 space-y-2">
                                    <div className="h-3 w-2/3 rounded bg-slate-100" />
                                    <div className="h-2.5 w-1/3 rounded bg-slate-100" />
                                </div>
                                <div className="h-6 w-16 rounded-full bg-slate-100" />
                            </div>
                        ))}
                    </div>
                )}

                {!isLoading && hasError && (
                    <div className="flex h-full min-h-40 items-center justify-center p-4 text-center sm:p-5">
                        <div className="max-w-sm rounded-2xl border border-rose-100 bg-rose-50/70 px-4 py-5">
                            <AlertCircle
                                size={22}
                                className="mx-auto text-rose-500"
                                aria-hidden="true"
                            />
                            <p className="mt-2 text-sm font-medium text-slate-700">
                                {errorMessage}
                            </p>
                            {errorDetail && (
                                <p className="mt-1 line-clamp-2 text-xs text-rose-600">
                                    {errorDetail}
                                </p>
                            )}
                        </div>
                    </div>
                )}

                {!isLoading && !hasError && safeItems.length === 0 && (
                    <div className="flex h-full min-h-40 items-center justify-center p-4 text-center sm:p-5">
                        <div className="max-w-sm rounded-2xl border border-slate-100 bg-slate-50/70 px-4 py-5">
                            <Inbox
                                size={22}
                                className="mx-auto text-slate-400"
                                aria-hidden="true"
                            />
                            <p className="mt-2 text-sm font-semibold text-slate-800">
                                {emptyTitle}
                            </p>
                            <p className="mt-1 text-xs text-slate-500">
                                {emptyDescription}
                            </p>
                        </div>
                    </div>
                )}

                {!isLoading && !hasError && safeItems.length > 0 && (
                    <div
                        className={`h-full transition-all duration-300 ease-out ${
                            shouldShowAllItems
                                ? "overflow-y-auto pr-1 [scrollbar-color:#c7d2fe_transparent] [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-indigo-200 [&::-webkit-scrollbar-track]:bg-transparent"
                                : "overflow-hidden"
                        }`}
                    >
                        {visibleItems.map((item, index) => (
                            <React.Fragment
                                key={getItemKey?.(item, index) || index}
                            >
                                {renderItem(item, index)}
                            </React.Fragment>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
};

export default DashboardRecentSection;
